import React, { useState, useEffect } from 'react';
import { Save, Lock, ArrowLeft, Link, LogOut, KeyRound, AlertTriangle, Database, Bell, ExternalLink, Copy, Check, Timer, Eye, EyeOff, Upload, Trash2, Image as ImageIcon } from 'lucide-react';
import { ContactSettings } from '../types'; 
import app, { auth, db, isFirebaseActive } from '../services/firebaseConfig';

interface AdminPanelProps {
  currentText: string;
  currentContactSettings: ContactSettings;
  currentGuideImages: string[];
  onLocalUpdate: (text: string, settings: ContactSettings, guideImages: string[]) => void;
  onBack: () => void;
}

const AdminPanel: React.FC<AdminPanelProps> = ({ 
  currentText, 
  currentContactSettings,
  currentGuideImages,
  onLocalUpdate,
  onBack 
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authMethod, setAuthMethod] = useState<'cloud' | 'pin'>(isFirebaseActive ? 'cloud' : 'pin');
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [subscriberCount, setSubscriberCount] = useState<number>(0);
  
  const [text, setText] = useState(currentText);
  const [contactForm, setContactForm] = useState<ContactSettings>(currentContactSettings);
  const [guideImages, setGuideImages] = useState<string[]>(currentGuideImages || []);

  const ADMIN_PIN = '123456';
  const projectId = "kyattobaht"; 

  useEffect(() => {
    if (isAuthenticated && isFirebaseActive && db) {
      db.collection('fcm_tokens').get().then((snap: any) => {
        setSubscriberCount(snap.size);
      }).catch((err: any) => console.error("Error fetching subs", err));
    }
  }, [isAuthenticated]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg('');

    if (authMethod === 'cloud' && isFirebaseActive && auth) {
      try {
        await auth.signInWithEmailAndPassword(email, password);
        setIsAuthenticated(true);
      } catch (err: any) {
        console.error("Login Error:", err);
        setErrorMsg('Email သို့မဟုတ် Password မှားယွင်းနေပါသည်။');
      }
    } else {
      if (pin === ADMIN_PIN) {
        setIsAuthenticated(true);
      } else {
        setErrorMsg('PIN နံပါတ် မှားယွင်းနေပါသည်။');
      }
    }
    setLoading(false);
  };

  const handleLogout = async () => {
    if (isFirebaseActive && auth && auth.currentUser) {
      await auth.signOut();
    }
    setIsAuthenticated(false);
    setPin('');
    setPassword('');
    onBack();
  };

  // Helper to compress images before upload
  const compressImage = (base64Str: string): Promise<string> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.src = base64Str;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const MAX_WIDTH = 1000;
        const MAX_HEIGHT = 1000;
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > MAX_WIDTH) {
            height *= MAX_WIDTH / width;
            width = MAX_WIDTH;
          }
        } else {
          if (height > MAX_HEIGHT) {
            width *= MAX_HEIGHT / height;
            height = MAX_HEIGHT;
          }
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0, width, height);
        // Use lower quality to reduce size (0.6 is good balance)
        resolve(canvas.toDataURL('image/jpeg', 0.6));
      };
    });
  };

  const handleSaveAll = async () => {
    setLoading(true);
    
    if (isFirebaseActive && db && authMethod === 'cloud') {
      try {
        // 1. Save Main Config (excluding images)
        await db.collection('settings').doc('app_config').set({
          announcementText: text,
          contactSettings: contactForm,
          lastUpdated: new Date().toISOString()
        }, { merge: true });
        
        // 2. Save Images to a separate collection to avoid 1MB limit
        const batch = db.batch();
        
        // First, clear old guides in the sub-collection if we want to sync perfectly
        // For simplicity in this logic, we delete all and re-add
        const existingGuides = await db.collection('guides').get();
        existingGuides.forEach((doc: any) => batch.delete(doc.ref));

        // Then add new ones
        guideImages.forEach((url, index) => {
          const docRef = db.collection('guides').doc();
          batch.set(docRef, {
            url: url,
            order: index,
            createdAt: new Date().toISOString()
          });
        });

        await batch.commit();
        
        onLocalUpdate(text, contactForm, guideImages);
        alert('✅ Cloud Update Successful!');
      } catch (err: any) {
        console.error("Cloud Save Error:", err);
        alert("❌ Cloud Save Failed!\n" + err.message);
      }
    } else {
      onLocalUpdate(text, contactForm, guideImages);
      alert('Saved Locally!');
    }
    setLoading(false);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64String = reader.result as string;
        const compressed = await compressImage(base64String);
        setGuideImages(prev => [...prev, compressed]);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = (index: number) => {
    if (window.confirm('ဒီပုံကို ဖျက်မှာ သေချာပါသလား?')) {
      setGuideImages(prev => prev.filter((_, i) => i !== index));
    }
  };

  const toggleVisibility = (platform: keyof Required<ContactSettings>['visibility']) => {
    setContactForm((prev: ContactSettings) => {
      const visibility = prev.visibility || {
        facebook: true, messenger: true, viber: true, telegram: true, line: true, phone: true, more: true
      };
      return {
        ...prev,
        visibility: {
          ...visibility,
          [platform]: !visibility[platform]
        }
      };
    });
  };

  const handleContactChange = (field: keyof ContactSettings, value: string) => {
    setContactForm((prev: ContactSettings) => ({
      ...prev,
      [field]: value
    }));
  };

  if (!isAuthenticated) {
    return (
      <div className="p-8 flex flex-col items-center justify-center min-h-[60vh]">
        <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-700 w-full max-w-sm">
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded-full">
              <Lock className="text-blue-500 dark:text-blue-400" size={24} />
            </div>
          </div>
          <h2 className="text-center text-xl font-bold text-gray-800 dark:text-white mb-2 font-myanmar">Admin Login</h2>
          <form onSubmit={handleLogin} className="space-y-4">
            {authMethod === 'cloud' ? (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full p-3 bg-gray-50 dark:bg-slate-700 rounded-lg border border-gray-200 dark:border-slate-600 dark:text-white outline-none" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Password</label>
                  <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full p-3 bg-gray-50 dark:bg-slate-700 rounded-lg border border-gray-200 dark:border-slate-600 dark:text-white outline-none" required />
                </div>
              </>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 font-myanmar">PIN Code ရိုက်ထည့်ပါ</label>
                <input 
                  type="password" 
                  value={pin} 
                  onChange={(e) => setPin(e.target.value)} 
                  className="w-full p-3 bg-gray-50 dark:bg-slate-700 rounded-lg border border-gray-200 dark:border-slate-600 text-center text-lg tracking-widest dark:text-white outline-none" 
                  maxLength={6} 
                  placeholder="••••••" 
                  required
                />
              </div>
            )}
            
            {errorMsg && <p className="text-red-500 text-sm text-center font-bold bg-red-50 p-2 rounded font-myanmar">{errorMsg}</p>}
            
            <button type="submit" disabled={loading} className="w-full bg-blue-600 text-white py-3 rounded-lg font-bold hover:bg-blue-700 disabled:opacity-50 font-myanmar transition-colors">
              {loading ? 'စစ်ဆေးနေသည်...' : 'ဆက်လက်လုပ်ဆောင်မည်'}
            </button>

            {isFirebaseActive && (
              <button
                type="button"
                onClick={() => {
                  setAuthMethod(authMethod === 'cloud' ? 'pin' : 'cloud');
                  setErrorMsg('');
                }}
                className="w-full text-blue-500 dark:text-blue-400 py-1 text-xs hover:underline flex items-center justify-center gap-1 font-myanmar"
              >
                {authMethod === 'cloud' ? (
                  <><KeyRound size={12} /> PIN Code ဖြင့်ဝင်မည်</>
                ) : (
                  'Email ဖြင့်ဝင်မည်'
                )}
              </button>
            )}
          </form>
        </div>
      </div>
    );
  }

  const visibility = contactForm.visibility || { facebook: true, messenger: true, viber: true, telegram: true, line: true, phone: true, more: true };

  return (
    <div className="p-4 pb-24">
      <div className="flex justify-between items-center mb-4">
        <button onClick={onBack} className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 font-medium text-sm">
          <ArrowLeft size={18} className="mr-1" /> Back
        </button>
        <button onClick={handleLogout} className="text-red-500 text-xs font-bold flex items-center gap-1">
          <LogOut size={14} /> Logout
        </button>
      </div>
      
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-700 p-6 space-y-8">
        
        <div className="flex justify-between items-start">
           <h2 className="text-xl font-bold text-gray-800 dark:text-white font-myanmar">Admin Dashboard</h2>
           {isFirebaseActive && (
              <span className="text-[10px] bg-green-100 text-green-700 px-2 py-1 rounded-full border border-green-200 flex items-center gap-1 font-bold">
                <Database size={10} /> Cloud Connected
              </span>
           )}
        </div>

        {/* PUSH NOTIFICATIONS */}
        {isFirebaseActive && (
          <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-100 dark:border-purple-800 rounded-xl p-4">
             <div className="flex items-center justify-between mb-2">
               <h3 className="font-bold text-purple-800 dark:text-purple-300 font-myanmar flex items-center gap-2 text-sm">
                 <Bell size={18} /> Push Notifications
               </h3>
               <span className="text-[10px] bg-purple-200 dark:bg-purple-800 text-purple-800 dark:text-white px-2 py-0.5 rounded-full font-bold">
                 {subscriberCount} Subscribers
               </span>
             </div>
             <p className="text-[11px] text-purple-600 dark:text-purple-400 font-myanmar mb-4 leading-relaxed">
               မိမိစေလွှတ်ရန် App တွင် Notification ဖွင့်ထားသူ ({subscriberCount}) ယောက် ရှိပါသည်။ စာပို့လိုပါက Firebase Console ကို သုံး၍ ပို့နိုင်ပါသည်။
             </p>
             <a 
               href={`https://console.firebase.google.com/u/0/project/${projectId}/notification/compose`} 
               target="_blank" 
               rel="noreferrer" 
               className="w-full bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold py-2.5 rounded-lg flex items-center justify-center gap-2 shadow-sm transition-colors"
             >
               Go to Firebase Console to Send <ExternalLink size={14} />
             </a>
          </div>
        )}

        {/* GUIDE MANAGEMENT */}
        <div>
          <h3 className="font-bold text-gray-800 dark:text-white font-myanmar flex items-center gap-2 mb-4">
            <span className="text-blue-500"><ImageIcon size={18} /></span> Guide Screenshots (လမ်းညွှန်ပုံများ)
          </h3>
          
          <div className="grid grid-cols-2 gap-3 mb-4">
            {guideImages.map((img, idx) => (
              <div key={idx} className="relative group aspect-[3/4] rounded-lg overflow-hidden border border-gray-200 dark:border-slate-700">
                <img src={img} alt="Guide Preview" className="w-full h-full object-cover" />
                <button 
                  onClick={() => removeImage(idx)}
                  className="absolute top-1 right-1 bg-red-500 text-white p-1.5 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Trash2 size={14} />
                </button>
                <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[10px] p-1 text-center font-bold">Step {idx + 1}</div>
              </div>
            ))}
            
            <label className="border-2 border-dashed border-gray-300 dark:border-slate-600 rounded-lg flex flex-col items-center justify-center aspect-[3/4] cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors">
              <Upload size={24} className="text-gray-400 mb-2" />
              <span className="text-[10px] font-bold text-gray-400 font-myanmar">ပုံတင်ရန်</span>
              <input type="file" accept="image/*" onChange={handleFileUpload} className="hidden" />
            </label>
          </div>
        </div>

        {/* ANNOUNCEMENT */}
        <div>
          <h3 className="font-bold text-gray-800 dark:text-white font-myanmar flex items-center gap-2 mb-4">
            <span className="text-orange-500">📢</span> Banner Announcement
          </h3>
          <textarea value={text} onChange={(e) => setText(e.target.value)} className="w-full p-3 bg-gray-50 dark:bg-slate-700 rounded-lg border border-gray-200 dark:border-slate-600 min-h-[80px] font-myanmar text-sm dark:text-white outline-none focus:ring-1 focus:ring-orange-500" />
        </div>

        {/* CONTACT LINKS & VISIBILITY */}
        <div className="space-y-4">
          <h3 className="font-bold text-gray-800 dark:text-white font-myanmar flex items-center gap-2">
             <span className="text-blue-500">🔗</span> Contact Links
          </h3>
          
          {[
            { label: 'Facebook URL', key: 'facebookUrl', vis: 'facebook' },
            { label: 'Messenger URL', key: 'messengerUrl', vis: 'messenger' },
            { label: 'Viber URL', key: 'viberUrl', vis: 'viber' },
            { label: 'Telegram URL', key: 'telegramUrl', vis: 'telegram' },
            { label: 'Line URL', key: 'lineUrl', vis: 'line' },
            { label: 'Phone Number', key: 'phoneNumber', vis: 'phone' },
          ].map((item) => (
            <div key={item.key} className="space-y-2 p-3 bg-gray-50 dark:bg-slate-900 rounded-xl border border-gray-100 dark:border-slate-700">
              <div className="flex justify-between items-center">
                <label className="text-xs font-bold text-gray-500 dark:text-gray-400">{item.label}</label>
                <button 
                  onClick={() => toggleVisibility(item.vis as any)} 
                  className={`px-3 py-1 rounded-full text-[10px] font-bold transition-all ${visibility[item.vis as keyof typeof visibility] ? 'bg-blue-100 text-blue-700 border border-blue-200' : 'bg-gray-200 text-gray-500 border border-gray-300'}`}
                >
                  {visibility[item.vis as keyof typeof visibility] ? 'Visible' : 'Hidden'}
                </button>
              </div>
              <div className="flex items-center bg-white dark:bg-slate-700 rounded-lg border border-gray-200 dark:border-slate-600 px-3">
                 <Link size={14} className="text-gray-400 mr-2" />
                 <input 
                   type="text" 
                   value={(contactForm as any)[item.key] || ''} 
                   onChange={(e) => handleContactChange(item.key as keyof ContactSettings, e.target.value)}
                   className="w-full bg-transparent p-2 text-sm outline-none dark:text-white font-sans"
                 />
              </div>
            </div>
          ))}

          <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-slate-900 rounded-xl border border-gray-100 dark:border-slate-700">
            <label className="text-xs font-bold text-gray-500 dark:text-gray-400">"More..." Button Visibility</label>
            <button onClick={() => toggleVisibility('more')} className={`px-3 py-1 rounded-full text-[10px] font-bold ${visibility.more ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-500'}`}>
              {visibility.more ? 'Visible' : 'Hidden'}
            </button>
          </div>
        </div>

        <button onClick={handleSaveAll} disabled={loading} className="w-full bg-green-600 hover:bg-green-700 text-white py-4 rounded-xl font-bold flex items-center justify-center gap-2 shadow-md disabled:opacity-50 transition-colors font-myanmar">
          {loading ? 'သိမ်းဆည်းနေသည်...' : <><Save size={20} /> Save to Cloud (All Users)</>}
        </button>
      </div>
    </div>
  );
};

export default AdminPanel;